// Dojo shop logic (Phase 6): buy with wallet coins, equip cosmetics.
// Modifiers are always active once owned; cosmetics are equipped per slot.

import { shopItem, type ShopItem } from '@/content/shop-items';
import type { SaveV2 } from '@/save/schema';

export function owns(save: SaveV2, itemId: string): boolean {
  return save.unlocks.includes(itemId);
}

export function canAfford(save: SaveV2, item: ShopItem): boolean {
  return save.wallet >= item.price;
}

/** Buy an item; returns false if already owned or unaffordable. */
export function buy(save: SaveV2, itemId: string): boolean {
  const item = shopItem(itemId);
  if (!item || owns(save, itemId) || !canAfford(save, item)) return false;
  save.wallet -= item.price;
  save.unlocks.push(itemId);
  // Cosmetics equip immediately on purchase
  if (item.kind !== 'modifier') save.equipped[item.kind] = itemId;
  return true;
}

/** Equip an owned cosmetic (or unequip by passing null). */
export function equip(save: SaveV2, slot: 'gi' | 'headband', itemId: string | null): boolean {
  if (itemId === null) {
    delete save.equipped[slot];
    return true;
  }
  const item = shopItem(itemId);
  if (!item || item.kind !== slot || !owns(save, itemId)) return false;
  save.equipped[slot] = itemId;
  return true;
}

export function equippedColor(save: SaveV2, slot: 'gi' | 'headband'): number | null {
  const id = save.equipped[slot];
  if (!id) return null;
  return shopItem(id)?.color ?? null;
}

/** Active gameplay modifiers = owned modifier items. */
export function activeModifiers(save: SaveV2): Set<string> {
  return new Set(save.unlocks.filter((id) => shopItem(id)?.kind === 'modifier'));
}
